import { Fragment, useState } from "react";
import { useNavigate } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useDatasheetOrders, useOrderBadges } from "@/lib/datasheets";

export function OrdersSection({ datasheetId }: { datasheetId: number }) {
  const navigate = useNavigate();
  const { data, isLoading } = useDatasheetOrders(datasheetId);
  const [expanded, setExpanded] = useState<number | null>(null);

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!data || data.length === 0) {
    return <p className="text-sm text-muted-foreground">No orders placed against this datasheet.</p>;
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b text-left">
          <th className="py-2">Date</th>
          <th>Status</th>
          <th>Job</th>
          <th>PO</th>
          <th className="text-right">Quantities</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {data.map((o) => (
          <Fragment key={o.datasheetUsageId}>
            <tr className="border-b last:border-0">
              <td className="py-2">{new Date(o.usageDate).toLocaleDateString()}</td>
              <td>
                <Badge variant={o.committed ? "secondary" : "outline"}>
                  {o.committed ? "Committed" : "Draft"}
                </Badge>
              </td>
              <td>
                {o.jobId ? (
                  <button
                    type="button"
                    className="text-primary underline"
                    onClick={() => void navigate(`/jobs/${o.jobId}`)}
                  >
                    {o.jobReferenceNo ?? `#${o.jobId}`}
                  </button>
                ) : "—"}
              </td>
              <td>{o.clientPurchaseOrderNo ?? "—"}</td>
              <td className="text-right">{o.sizeQuantities.filter((q) => q !== 0).join(", ") || "—"}</td>
              <td className="text-right">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setExpanded((cur) => (cur === o.datasheetUsageId ? null : o.datasheetUsageId))}
                >
                  View badges
                </Button>
              </td>
            </tr>
            {expanded === o.datasheetUsageId ? (
              <tr>
                <td colSpan={6}>
                  <OrderBadgesRow datasheetId={datasheetId} usageId={o.datasheetUsageId} />
                </td>
              </tr>
            ) : null}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

function OrderBadgesRow({ datasheetId, usageId }: { datasheetId: number; usageId: number }) {
  const { data, isLoading } = useOrderBadges(datasheetId, usageId, true);
  if (isLoading) return <p className="p-2 text-xs text-muted-foreground">Loading…</p>;
  if (!data || data.length === 0) {
    return <p className="p-2 text-xs text-muted-foreground">No badge content (quantity-only order).</p>;
  }
  return (
    <table className="w-full text-xs">
      <tbody>
        {data.map((b) => (
          <tr key={b.datasheetOrderBadgeId}>
            <td className="py-1">{b.fieldValues.join(" · ") || "—"}</td>
            <td className="text-right">×{b.quantity}</td>
            <td className="text-right">size #{b.prepaidSize}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
